import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  FormControlLabel,
  Switch,
  Tooltip,
  CircularProgress,
  TablePagination,
  InputAdornment
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import api from '../../../services/api';
import { useAuth } from '../../../context/AuthContext';

const ProductionCoordinatorManagement = () => {
  const { enqueueSnackbar } = useSnackbar();
  // eslint-disable-next-line no-unused-vars
  const { token } = useAuth();
  
  // State for production coordinators data
  const [coordinators, setCoordinators] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // State for pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  
  // State for search
  const [searchTerm, setSearchTerm] = useState('');
  
  // State for coordinator form dialog
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogMode, setDialogMode] = useState('add'); // 'add' or 'edit'
  const [formData, setFormData] = useState({
    id: null,
    name: '',
    email: '',
    phone: '',
    isActive: true
  });
  const [formErrors, setFormErrors] = useState({});
  
  // State for delete confirmation dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [coordinatorToDelete, setCoordinatorToDelete] = useState(null);
  
  // Fetch coordinators on component mount and when pagination/search changes
  useEffect(() => {
    fetchCoordinators();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, searchTerm]);
  
  // Fetch coordinators from API
  const fetchCoordinators = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await api.get('/production-coordinators', {
        params: {
          page: page + 1, // API uses 1-based indexing
          limit: rowsPerPage,
          search: searchTerm
        }
      });
      
      console.log('Production Coordinators API response:', response.data);
      
      if (response.data) {
        // Backend mengembalikan data dengan properti productionCoordinators, bukan coordinators
        setCoordinators(response.data.productionCoordinators || []);
        setTotalCount(response.data.productionCoordinators?.length || 0);
      }
    } catch (err) {
      console.error('Error fetching production coordinators:', err);
      setError('Gagal memuat data koordinator produksi. Silakan coba lagi.');
      enqueueSnackbar('Gagal memuat data koordinator produksi', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };
  
  // Handle dialog open for adding new coordinator
  const handleAddCoordinator = () => {
    setFormData({
      id: null,
      name: '',
      email: '',
      phone: '',
      isActive: true
    });
    setFormErrors({});
    setDialogMode('add');
    setOpenDialog(true);
  };
  
  // Handle dialog open for editing coordinator
  const handleEditCoordinator = (coordinator) => {
    setFormData({
      id: coordinator.id,
      name: coordinator.name || '',
      email: coordinator.email || '',
      phone: coordinator.phone || '',
      isActive: coordinator.isActive
    });
    setFormErrors({});
    setDialogMode('edit');
    setOpenDialog(true);
  };
  
  // Handle dialog close
  const handleCloseDialog = () => {
    setOpenDialog(false);
  };
  
  // Handle form input change
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error for this field
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };
  
  // Handle switch change for isActive
  const handleSwitchChange = (e) => {
    setFormData(prev => ({
      ...prev,
      isActive: e.target.checked
    }));
  };
  
  // Validate form
  const validateForm = () => {
    const errors = {};
    
    if (!formData.name.trim()) {
      errors.name = 'Nama koordinator produksi wajib diisi';
    }
    
    if (formData.email && !/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = 'Format email tidak valid';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };
  
  // Handle form submit
  const handleSubmit = async () => {
    if (!validateForm()) return;
    
    try {
      setLoading(true);
      
      if (dialogMode === 'add') {
        // Create new coordinator
        await api.post('/production-coordinators', formData);
        enqueueSnackbar('Koordinator produksi berhasil ditambahkan', { variant: 'success' });
      } else {
        // Update existing coordinator
        await api.put(`/production-coordinators/${formData.id}`, formData);
        enqueueSnackbar('Koordinator produksi berhasil diperbarui', { variant: 'success' });
      }
      
      // Refresh coordinators list
      fetchCoordinators();
      handleCloseDialog();
    } catch (err) {
      console.error('Error saving production coordinator:', err);
      enqueueSnackbar('Gagal menyimpan data koordinator produksi', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };
  
  // Handle delete confirmation dialog open
  const handleDeleteConfirmOpen = (coordinator) => {
    setCoordinatorToDelete(coordinator);
    setDeleteDialogOpen(true);
  };
  
  // Handle delete confirmation dialog close
  const handleDeleteConfirmClose = () => {
    setDeleteDialogOpen(false);
    setCoordinatorToDelete(null);
  };
  
  // Handle coordinator deletion
  const handleDeleteCoordinator = async () => {
    if (!coordinatorToDelete) return;
    
    try {
      setLoading(true);
      
      await api.delete(`/production-coordinators/${coordinatorToDelete.id}`);
      enqueueSnackbar('Koordinator produksi berhasil dihapus', { variant: 'success' });
      
      // Refresh coordinators list
      fetchCoordinators();
    } catch (err) {
      console.error('Error deleting production coordinator:', err);
      
      // Handle case where coordinator is in use
      if (err.response && err.response.status === 400 && err.response.data.message.includes('digunakan')) {
        enqueueSnackbar('Koordinator produksi tidak dapat dihapus karena sedang digunakan dalam proyek', { 
          variant: 'error',
          autoHideDuration: 5000
        });
      } else {
        enqueueSnackbar('Gagal menghapus koordinator produksi', { variant: 'error' });
      }
    } finally {
      setLoading(false);
      handleDeleteConfirmClose();
    }
  };
  
  // Handle page change
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };
  
  // Handle rows per page change
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  
  // Handle search input change
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setPage(0); // Reset to first page when searching
  };
  
  // Handle refresh button click
  const handleRefresh = () => {
    setSearchTerm('');
    fetchCoordinators();
  };
  
  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Production Coordinator Management
      </Typography>
      
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <TextField
          placeholder="Search coordinators..."
          variant="outlined"
          size="small"
          value={searchTerm}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ width: 300 }}
        />
        
        <Box>
          <Button 
            variant="outlined" 
            startIcon={<RefreshIcon />} 
            onClick={handleRefresh}
            sx={{ mr: 1 }}
          >
            Refresh
          </Button>
          <Button 
            variant="contained" 
            color="primary" 
            startIcon={<AddIcon />} 
            onClick={handleAddCoordinator}
          >
            Add Coordinator
          </Button>
        </Box>
      </Box>
      
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>No</TableCell>
              <TableCell>Coordinator Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Phone</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && coordinators.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <CircularProgress size={30} />
                </TableCell>
              </TableRow>
            ) : error ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography color="error">{error}</Typography>
                </TableCell>
              </TableRow>
            ) : coordinators.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography>No production coordinator data available</Typography>
                </TableCell>
              </TableRow>
            ) : (
              coordinators.map((coordinator, index) => (
                <TableRow key={coordinator.id}>
                  <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                  <TableCell>{coordinator.name}</TableCell>
                  <TableCell>{coordinator.email || '-'}</TableCell>
                  <TableCell>{coordinator.phone || '-'}</TableCell>
                  <TableCell>
                    {coordinator.isActive ? (
                      <Typography color="success.main">Active</Typography>
                    ) : (
                      <Typography color="text.disabled">Inactive</Typography>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="Edit">
                      <IconButton 
                        color="primary" 
                        onClick={() => handleEditCoordinator(coordinator)}
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Hapus">
                      <IconButton 
                        color="error" 
                        onClick={() => handleDeleteConfirmOpen(coordinator)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      
      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={totalCount}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        labelRowsPerPage="Baris per halaman:"
        labelDisplayedRows={({ from, to, count }) => `${from}-${to} dari ${count}`}
      />
      
      {/* Add/Edit Coordinator Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {dialogMode === 'add' ? 'Add New Production Coordinator' : 'Edit Production Coordinator'}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            name="name"
            label="Coordinator Name *"
            type="text"
            fullWidth
            variant="outlined"
            value={formData.name}
            onChange={handleInputChange}
            error={!!formErrors.name}
            helperText={formErrors.name}
            sx={{ mb: 2, mt: 1 }}
          />
          <TextField
            margin="dense"
            name="email"
            label="Email"
            type="email"
            fullWidth
            variant="outlined"
            value={formData.email}
            onChange={handleInputChange}
            error={!!formErrors.email}
            helperText={formErrors.email}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            name="phone"
            label="Phone"
            type="text"
            fullWidth
            variant="outlined"
            value={formData.phone}
            onChange={handleInputChange}
            sx={{ mb: 2 }}
          />
          <FormControlLabel
            control={
              <Switch
                checked={formData.isActive}
                onChange={handleSwitchChange}
                color="primary"
              />
            }
            label="Active"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained" 
            color="primary"
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleDeleteConfirmClose}>
        <DialogTitle>Delete Confirmation</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete production coordinator "{coordinatorToDelete?.name}"? 
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteConfirmClose}>Cancel</Button>
          <Button 
            onClick={handleDeleteCoordinator} 
            color="error" 
            variant="contained"
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProductionCoordinatorManagement;
